import { Stack, Loader, Alert } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useAuthStore, useCan } from '@/store/auth'
import { useManagedOrganizations } from '@/hooks/useOrganizations'
import { OrganizationManager } from '@/components/admin/OrganizationManager'

interface TenantRow {
  id: number
  name: string
  environment: string
}

export function Organizacoes() {
  const { user, selectedTenantId } = useAuthStore()
  const can = useCan()
  // Espelho do gate admin do backend (_require_caller_is_admin / admin:platform).
  // Não-admin que gere org(s) — gestor/responsável — entra em "modo gestor":
  // vê apenas as orgs que gere, com as ações admin-only escondidas.
  const isAdmin = can('admin', 'platform')
  const { data: managedOrgs = [], isLoading: loadingManaged } = useManagedOrganizations()

  // Deep-link (spec 2026-07-24 §3.5): ?org={id}&tab=solicitacoes&req={id}
  // → expande o card, abre a aba e destaca a solicitação.
  const [searchParams] = useSearchParams()
  const deepLinkOrgId = searchParams.get('org') ? Number(searchParams.get('org')) : null
  const deepLinkTab = searchParams.get('tab')
  const deepLinkReqId = searchParams.get('req') ? Number(searchParams.get('req')) : null

  // /v1/admin/tenants é admin-gated — só busca para admin. O gestor não
  // precisa: a gestão de tenants da org (adicionar/remover) segue admin-only.
  const { data: tenants, isLoading } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: async () => {
      const res = await api.get<{ items: TenantRow[] }>('/v1/admin/tenants')
      return res.data.items
    },
    enabled: isAdmin,
  })

  const tenantOptions = (tenants ?? []).map(t => ({
    value: String(t.id),
    label: `[${t.environment.toUpperCase()}] ${t.name}`,
  }))

  const loadingPage = isAdmin ? isLoading : loadingManaged

  return (
    <Stack gap="md" p="xl">

      {loadingPage ? (
        <Loader size="sm" />
      ) : !isAdmin && managedOrgs.length === 0 ? (
        <Alert color="gray" variant="light" icon={<IconLock size={16} />}>
          Você não gerencia nenhuma Organização. Peça a um administrador para
          atribuí-lo como gestor ou responsável de uma Organização.
        </Alert>
      ) : (
        <OrganizationManager
          tenants={tenantOptions}
          canEditJira={isAdmin}
          isAdmin={isAdmin}
          visibleOrgIds={isAdmin ? undefined : managedOrgs.map((o) => o.id)}
          deepLinkOrgId={deepLinkOrgId}
          deepLinkTab={deepLinkTab}
          deepLinkReqId={deepLinkReqId}
        />
      )}
    </Stack>
  )
}
